import { useState } from 'react';
import {
  OpsEmpty,
  OpsError,
  OpsLoading,
  OpsPagination,
  OpsTable,
  Th,
  formatOpsDate,
} from '@/components/OpsTable';
import { ApiCallError } from '@/lib/api';
import {
  THREAD_STATUSES,
  useOpsThreads,
  type ThreadStatus,
} from '@/hooks/useOpsDashboard';

export function Threads() {
  const [status, setStatus] = useState<ThreadStatus | 'all'>('all');
  const [offset, setOffset] = useState(0);

  const { data, isLoading, error } = useOpsThreads({ status, offset });
  const items = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Threads</h1>

      <div className="mt-6">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ThreadStatus | 'all');
            setOffset(0);
          }}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        >
          {THREAD_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <OpsError message={error instanceof ApiCallError ? error.message : 'Could not load threads.'} />
      )}

      {isLoading ? (
        <OpsLoading />
      ) : items.length === 0 ? (
        <OpsEmpty description="No threads yet." />
      ) : (
        <>
          <OpsTable>
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <Th>Subject</Th>
                <Th>Status</Th>
                <Th>Identifier</Th>
                <Th>Created</Th>
                <Th>Updated</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
              {items.map((item) => (
                <tr key={item.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="max-w-xs truncate px-3 py-4 text-sm text-gray-900 first:pl-4 dark:text-white sm:first:pl-6">
                    {item.subject?.trim() || '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {item.status}
                  </td>
                  <td className="max-w-xs truncate px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {item.communicationIdentifier.replace(/^urn:uuid:/i, '').slice(0, 12)}…
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {formatOpsDate(item.createdAt)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {formatOpsDate(item.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </OpsTable>
          {pagination && (
            <OpsPagination
              total={pagination.total}
              limit={pagination.limit}
              offset={pagination.offset}
              onOffsetChange={setOffset}
            />
          )}
        </>
      )}
    </div>
  );
}
